import axios from 'axios';

/**
 * Panel auth client — local stub for CI (until @cavicode/sso-webclient NPM package is published).
 * Ported from legacy CaviCode-SSO-Client/frontend/panel-auth.ts.
 */

export interface PanelCurrentUser {
    subject: string;
    email: string;
    displayName: string;
    roles: string[];
    role: string;
}

export type PanelAuthMePayload = {
    subject: string;
    email: string;
    displayName: string;
    roles: string[];
};

export type PanelAuthHttpClient = {
    get<T>(path: string): Promise<{ data: T }>;
    post<T>(path: string): Promise<{ data: T; status: number }>;
};

const defaultRolePriority = ['admin', 'operator', 'viewer'] as const;

export function pickPrimaryRole(
    roles: string[],
    priority: readonly string[] = defaultRolePriority,
): string {
    for (const role of priority) {
        if (roles.includes(role)) {
            return role;
        }
    }
    return roles[0] ?? priority[priority.length - 1] ?? 'viewer';
}

export function normalizePanelUser(
    payload: PanelAuthMePayload,
    rolePriority?: readonly string[],
): PanelCurrentUser {
    return {
        subject: payload.subject,
        email: payload.email,
        displayName: payload.displayName,
        roles: payload.roles,
        role: pickPrimaryRole(payload.roles, rolePriority),
    };
}

export function buildPanelLoginUrl(returnUrl = '/', forceLogin = false): string {
    const params = new URLSearchParams();
    if (returnUrl && returnUrl !== '/') params.set('return', returnUrl);
    if (forceLogin) params.set('force', '1');
    const qs = params.toString();
    return qs ? `/auth/login?${qs}` : '/auth/login';
}

export function createPanelAuthApi(http: PanelAuthHttpClient) {
    return {
        async getCurrentUser(): Promise<PanelCurrentUser | null> {
            try {
                const { data } = await http.get<PanelAuthMePayload>('/auth/me');
                return normalizePanelUser(data);
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.status === 401) return null;
                throw error;
            }
        },
        buildLoginUrl: buildPanelLoginUrl,
        async logout(force = false): Promise<string | undefined> {
            const { data, status } = await http.post<PanelAuthLogoutResponse>('/auth/logout');
            // If the server returns an end-session URL, the caller should redirect to it.
            if (status === 200 && data?.endSessionUrl) {
                return data.endSessionUrl;
            }
            return undefined;
        },
    };
}

/** Response from POST /auth/logout — may contain endSessionUrl for OIDC RP-initiated logout. */
export interface PanelAuthLogoutResponse {
    endSessionUrl?: string;
}
